import React, { useState, useEffect } from 'react';
import {
  Text,
  Heading,
  Badge,
  Icon,
  Button,
  Container,
  Input
} from './ui/index.jsx';

const TechnicalArchitecture = ({ analysisResponse, research, validation, platform, containerId = 'technicalArchitecture' }) => {
  const [expandedLayers, setExpandedLayers] = useState({});
  const [copyStatus, setCopyStatus] = useState({});
  const [selectedLayerKey, setSelectedLayerKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useState('comfortable');


  // Extract blueprint from analysis response - NO MOCK DATA
  const extractBlueprint = (analysisResponse) => {

    if (!analysisResponse) {
      return null;
    }

    // PRIORITY 1: Check for Agent response format - direct cursorBlueprint
    if (analysisResponse?.cursorBlueprint && typeof analysisResponse.cursorBlueprint === 'object' && Object.keys(analysisResponse.cursorBlueprint).length > 0) {
      const converted = convertCursorBlueprint(analysisResponse.cursorBlueprint);
      if (converted) {
        return converted;
      }
    }

    // PRIORITY 2: Check for Agent response content format - cursorBlueprint in parsed content
    if (analysisResponse?.response?.content) {
      try {
        const parsedContent = typeof analysisResponse.response.content === 'string'
          ? JSON.parse(analysisResponse.response.content)
          : analysisResponse.response.content;

        if (parsedContent?.cursorBlueprint && typeof parsedContent.cursorBlueprint === 'object' && Object.keys(parsedContent.cursorBlueprint).length > 0) {
          const converted = convertCursorBlueprint(parsedContent.cursorBlueprint);
          if (converted) {
            return converted;
          }
        }
      } catch (parseError) {
      }
    }

    // PRIORITY 3: Check nested analysis.cursorBlueprint (legacy format)
    if (analysisResponse?.analysis?.cursorBlueprint && typeof analysisResponse.analysis.cursorBlueprint === 'object' && Object.keys(analysisResponse.analysis.cursorBlueprint).length > 0) {
      const converted = convertCursorBlueprint(analysisResponse.analysis.cursorBlueprint);
      if (converted) {
        return converted;
      }
    }

    // NO MOCK DATA - No fallback to research data
    if (research) {
      return null;
    }

    return null;
  };

  // Convert Cursor blueprint to layered format - NO MOCK DATA
  const convertCursorBlueprint = (cursorBlueprint) => {

    if (!cursorBlueprint) {
      return null;
    }

    // Check if it's in the NEW 4-layer + crossCuttingConcerns format
    const hasNew4LayerFormat = [
      'presentationLayer', 'businessLayer', 'persistenceLayer', 'databaseLayer'
    ].every(layer => cursorBlueprint[layer] && cursorBlueprint[layer] !== null) &&
      cursorBlueprint.crossCuttingConcerns;

    if (hasNew4LayerFormat) {
      // New format - pass through as-is
      return cursorBlueprint;
    }

    // Check if it's in the OLD 5-layer format with securityLayer at root
    const hasOld5LayerFormat = [
      'presentationLayer', 'businessLayer', 'persistenceLayer',
      'databaseLayer', 'securityLayer'
    ].every(layer => cursorBlueprint[layer] && cursorBlueprint[layer] !== null);

    if (hasOld5LayerFormat) {
      // Old format - pass through (sanitizeBlueprint will convert)
      return cursorBlueprint;
    }

    // Check if this is Cursor format that needs conversion
    const hasCursorLayers = cursorBlueprint.frontendLayer || cursorBlueprint.backendLayer || cursorBlueprint.deploymentLayer;

    // Check if this has any valid layer structure (more flexible validation)
    const hasAnyLayerFormat = [
      'presentationLayer', 'businessLayer', 'persistenceLayer',
      'databaseLayer', 'securityLayer'
    ].some(layer => cursorBlueprint[layer] && cursorBlueprint[layer] !== null) ||
      cursorBlueprint.crossCuttingConcerns;

    const hasValidStructure = hasCursorLayers || hasAnyLayerFormat || cursorBlueprint.projectStructure;

    if (!hasValidStructure) {
      return null;
    }


    // Convert from Cursor structure to 4-layer + crossCuttingConcerns format

    // Extract rich data from Cursor blueprint structure
    const converted = {
      presentationLayer: null,
      businessLayer: null,
      persistenceLayer: null,
      databaseLayer: cursorBlueprint.databaseLayer || null,
      // Handle both old and new format for security/cross-cutting
      securityLayer: cursorBlueprint.securityLayer || null,
      // Pass through crossCuttingConcerns if it exists in new format
      crossCuttingConcerns: cursorBlueprint.crossCuttingConcerns || null
    };

    // Enhanced PresentationLayer extraction with detailed component info
    if (cursorBlueprint.presentationLayer || cursorBlueprint.frontendLayer) {
      const frontendData = cursorBlueprint.presentationLayer || cursorBlueprint.frontendLayer;

      // Extract detailed component information - NO MOCK DATA
      const detailedComponents = frontendData.components ? frontendData.components.map(comp => ({
        name: comp.name,
        purpose: comp.purpose,
        file: comp.file,
        props: comp.props,
        implementation: comp.implementation
      })).filter(comp => comp.name) : []; // Only include components with actual names

      converted.presentationLayer = {
        ...frontendData,
        projectStructure: cursorBlueprint.projectStructure ? {
          folders: cursorBlueprint.projectStructure.folders,
          configuration: cursorBlueprint.projectStructure.configuration,
          environment: cursorBlueprint.projectStructure.environment
        } : null,
        components: detailedComponents,
        detailedComponents: {
          count: detailedComponents.length,
          list: detailedComponents,
          framework: frontendData.framework,
          styling: frontendData.styling,
          stateManagement: frontendData.stateManagement
        },
        screens: frontendData.screens,
        userExperience: frontendData.userExperience,
        accessibility: frontendData.accessibility
      };
    }

    // Enhanced BusinessLayer extraction with detailed endpoint info
    if (cursorBlueprint.businessLayer || cursorBlueprint.backendLayer) {
      const backendData = cursorBlueprint.businessLayer || cursorBlueprint.backendLayer;

      // Extract detailed endpoint information - NO MOCK DATA
      const detailedEndpoints = backendData.endpoints ? backendData.endpoints.map(endpoint => ({
        route: endpoint.route,
        method: endpoint.method,
        purpose: endpoint.purpose,
        file: endpoint.file,
        middleware: endpoint.middleware,
        parameters: endpoint.parameters,
        response: endpoint.response
      })).filter(endpoint => endpoint.route) : []; // Only include endpoints with actual routes

      converted.businessLayer = {
        ...backendData,
        architecture: backendData.architecture,
        endpoints: detailedEndpoints,
        detailedEndpoints: {
          count: detailedEndpoints.length,
          list: detailedEndpoints
        },
        database: backendData.database,
        authentication: backendData.authentication,
        services: backendData.services,
        workflows: backendData.workflows,
        businessRules: backendData.businessRules,
        middleware: backendData.middleware
      };
    }





    // Enhanced PersistenceLayer extraction with deployment details - NO MOCK DATA
    if (!converted.persistenceLayer) {
      const backendData = cursorBlueprint.backendLayer;
      const deploymentData = cursorBlueprint.deploymentLayer;

      // Only create persistenceLayer if we have actual data
      if (backendData?.database || deploymentData?.database || cursorBlueprint.persistenceLayer) {
        converted.persistenceLayer = {
          dataAccess: cursorBlueprint.persistenceLayer?.dataAccess || {
            orm: backendData?.database,
            patterns: cursorBlueprint.persistenceLayer?.dataAccess?.patterns,
            caching: cursorBlueprint.persistenceLayer?.dataAccess?.caching,
            optimization: cursorBlueprint.persistenceLayer?.dataAccess?.optimization
          },
          migrations: cursorBlueprint.persistenceLayer?.migrations,
          seedData: cursorBlueprint.persistenceLayer?.seedData,
          deployment: deploymentData?.database
        };
      }
    }

    // Enhanced DatabaseLayer extraction with rich schema details - NO MOCK DATA
    if (!converted.databaseLayer && (cursorBlueprint.databaseLayer || cursorBlueprint.backendLayer?.database)) {
      converted.databaseLayer = {
        schema: cursorBlueprint.databaseLayer?.schema ? {
          description: cursorBlueprint.databaseLayer.schema.description,
          tables: cursorBlueprint.databaseLayer.schema.tables,
          relationships: cursorBlueprint.databaseLayer.schema.relationships,
          views: cursorBlueprint.databaseLayer.schema.views
        } : {
          description: cursorBlueprint.backendLayer?.database,
          tables: [],
          relationships: [],
          views: null
        },
        performance: cursorBlueprint.databaseLayer?.performance,
        backup: cursorBlueprint.databaseLayer?.backup
      };
    }

    // Enhanced SecurityLayer extraction with deployment security - NO MOCK DATA
    if (!converted.securityLayer) {
      const backendData = cursorBlueprint.backendLayer;
      const deploymentData = cursorBlueprint.deploymentLayer;
      const securityData = cursorBlueprint.securityLayer;

      // Only create securityLayer if we have actual data
      if (backendData?.authentication || deploymentData || securityData) {
        converted.securityLayer = {
          authentication: securityData?.authentication || {
            strategy: backendData?.authentication,
            providers: securityData?.authentication?.providers,
            session: securityData?.authentication?.session
          },
          authorization: securityData?.authorization,
          dataProtection: securityData?.dataProtection,
          deployment: {
            environment: deploymentData?.environment,
            monitoring: deploymentData?.monitoring,
            cicd: deploymentData?.cicd
          }
        };
      }
    }

    // Extract comprehensive deployment information into all relevant layers - NO MOCK DATA
    if (cursorBlueprint.deploymentLayer) {
      const deploymentData = cursorBlueprint.deploymentLayer;

      if (converted.presentationLayer && deploymentData.hosting) {
        converted.presentationLayer.deployment = {
          hosting: deploymentData.hosting,
          cdn: deploymentData.cdn,
          performance: deploymentData.performance
        };
      }

      if (converted.businessLayer && (deploymentData.cicd || deploymentData.monitoring)) {
        converted.businessLayer.deployment = {
          cicd: deploymentData.cicd,
          testing: deploymentData.testing,
          monitoring: deploymentData.monitoring
        };
      }

      if (converted.persistenceLayer && !converted.persistenceLayer.deployment && deploymentData.database) {
        converted.persistenceLayer.deployment = {
          database: deploymentData.database,
          backup: deploymentData.backup,
          performance: deploymentData.performance
        };
      }

      if (converted.securityLayer && !converted.securityLayer.deployment && (deploymentData.environment || deploymentData.monitoring)) {
        converted.securityLayer.deployment = {
          environment: deploymentData.environment,
          monitoring: deploymentData.monitoring,
          compliance: deploymentData.compliance
        };
      }
    }


    return converted;
  };

  // Sanitize blueprint - NO MOCK DATA
  const sanitizeBlueprint = (blueprint) => {

    if (!blueprint) {
      return null;
    }

    // 4 CORE LAYERS + 4 CROSS-CUTTING CONCERNS (industry standard)
    // Backward compatible: merges old layer names into new structure
    const sanitized = {
      // 4 CORE LAYERS (merge old format into new)
      presentationLayer: {
        ...blueprint.presentationLayer,
        ...blueprint.frontendLayer, // Merge frontendLayer if exists
      },
      businessLayer: {
        ...blueprint.businessLayer,
        ...blueprint.backendLayer, // Merge backendLayer if exists
      },
      persistenceLayer: blueprint.persistenceLayer,
      databaseLayer: blueprint.databaseLayer,
      // 4 CROSS-CUTTING CONCERNS (handle both new and old format)
      crossCuttingConcerns: blueprint.crossCuttingConcerns || {
        security: blueprint.securityLayer || null,
        projectStructure: blueprint.projectStructure || null,
        deployment: blueprint.deploymentLayer || null,
        testing: blueprint.testingStrategy || null,
      },
    };


    // Ensure array fields are properly handled
    const arrayFields = ['screens', 'components', 'services', 'workflows', 'tables', 'relationships'];
    arrayFields.forEach(key => {
      Object.keys(sanitized).forEach(layer => {
        if (sanitized[layer] && sanitized[layer][key] && !Array.isArray(sanitized[layer][key])) {
          sanitized[layer][key] = [];
        }
      });
    });

    return sanitized;
  };

  // Get the blueprint for rendering
  const blueprint = analysisResponse ? extractBlueprint(analysisResponse) : null;
  const sanitizedBlueprint = blueprint ? sanitizeBlueprint(blueprint) : null;

  // Initialize selected layer when blueprint becomes available
  useEffect(() => {
    if (!sanitizedBlueprint) return;
    // 4 CORE LAYERS + 4 CROSS-CUTTING CONCERNS in display order
    const orderedKeys = [
      // Core layers first
      'presentationLayer', 'businessLayer', 'persistenceLayer', 'databaseLayer',
      // Cross-cutting concerns
      'security', 'projectStructure', 'deployment', 'testing'
    ];
    // Check both root level (core) and crossCuttingConcerns (cross-cutting)
    const firstAvailable = orderedKeys.find(k => {
      if (['presentationLayer', 'businessLayer', 'persistenceLayer', 'databaseLayer'].includes(k)) {
        return !!sanitizedBlueprint[k];
      }
      return !!sanitizedBlueprint.crossCuttingConcerns?.[k];
    });
    setSelectedLayerKey(prev => prev || firstAvailable || null);
  }, [sanitizedBlueprint]);

  // Layer accent colors for better differentiation
  const getLayerAccent = (key) => {
    switch (key) {
      // 4 Core layers
      case 'presentationLayer':
        return { icon: 'text-[#7B61FF]', border: 'border-[#7B61FF]', bg: 'bg-[#7B61FF]/10' };
      case 'businessLayer':
        return { icon: 'text-green-400', border: 'border-green-400', bg: 'bg-green-400/10' };
      case 'persistenceLayer':
        return { icon: 'text-[#FFB800]', border: 'border-[#FFB800]', bg: 'bg-[#FFB800]/10' };
      case 'databaseLayer':
        return { icon: 'text-sky-400', border: 'border-sky-400', bg: 'bg-sky-400/10' };
      // 4 Cross-cutting concerns
      case 'security':
        return { icon: 'text-red-400', border: 'border-red-400', bg: 'bg-red-400/10' };
      case 'projectStructure':
        return { icon: 'text-amber-400', border: 'border-amber-400', bg: 'bg-amber-400/10' };
      case 'deployment':
        return { icon: 'text-pink-400', border: 'border-pink-400', bg: 'bg-pink-400/10' };
      case 'testing':
        return { icon: 'text-emerald-400', border: 'border-emerald-400', bg: 'bg-emerald-400/10' };
      default:
        return { icon: 'text-gray-400', border: 'border-white/10', bg: 'bg-white/5' };
    }
  };

  // Export helpers
  const handleExportJSON = () => {
    if (!sanitizedBlueprint) return;
    const blob = new Blob([JSON.stringify(sanitizedBlueprint, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'technical-architecture.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateMarkdownSummary = (bp) => {
    const lines = [];
    lines.push('# Technical Architecture Summary');
    lines.push('\n## Core Layers');
    const present = (arr) => Array.isArray(arr) && arr.length > 0;

    // 4 Core layers
    if (bp.presentationLayer) {
      const pl = bp.presentationLayer;
      lines.push('\n### Presentation Layer');
      if (pl.framework) lines.push(`- Framework: ${pl.framework}`);
      if (pl.styling) lines.push(`- Styling: ${pl.styling}`);
      if (present(pl.detailedComponents?.list || pl.components)) lines.push(`- Components: ${(pl.detailedComponents?.list || pl.components || []).length}`);
      if (present(pl.screens)) lines.push(`- Screens: ${pl.screens.length}`);
      if (pl.stateManagement) lines.push(`- State Management: ${pl.stateManagement}`);
    }
    if (bp.businessLayer) {
      const bl = bp.businessLayer;
      lines.push('\n### Business Layer');
      if (bl.architecture) lines.push(`- Architecture: ${bl.architecture}`);
      if (present(bl.detailedEndpoints?.list || bl.endpoints)) lines.push(`- Endpoints: ${(bl.detailedEndpoints?.list || bl.endpoints || []).length}`);
      if (present(bl.services)) lines.push(`- Services: ${bl.services.length}`);
      if (present(bl.domainEntities)) lines.push(`- Domain Entities: ${bl.domainEntities.length}`);
    }
    if (bp.persistenceLayer) {
      const ps = bp.persistenceLayer;
      lines.push('\n### Persistence Layer');
      if (ps.pattern) lines.push(`- Pattern: ${ps.pattern}`);
      if (ps.dataAccess?.orm) lines.push(`- ORM: ${ps.dataAccess.orm}`);
      if (present(ps.dataAccess?.patterns)) lines.push(`- Patterns: ${ps.dataAccess.patterns.join(', ')}`);
      if (present(ps.repositories)) lines.push(`- Repositories: ${ps.repositories.length}`);
    }
    if (bp.databaseLayer) {
      const db = bp.databaseLayer;
      lines.push('\n### Database Layer');
      if (db.database) lines.push(`- Database: ${db.database}`);
      if (db.schema?.description) lines.push(`- Schema: ${db.schema.description}`);
      if (present(db.schema?.tables)) lines.push(`- Tables: ${db.schema.tables.length}`);
      if (present(db.schema?.relationships)) lines.push(`- Relationships: ${db.schema.relationships.length}`);
    }

    // 4 Cross-cutting concerns
    const cc = bp.crossCuttingConcerns || {};
    if (cc.security || cc.projectStructure || cc.deployment || cc.testing) {
      lines.push('\n## Cross-Cutting Concerns');
    }

    if (cc.security) {
      const sec = cc.security;
      lines.push('\n### Security');
      if (sec.authentication?.strategy || sec.authentication?.provider) lines.push(`- Auth: ${sec.authentication.strategy || sec.authentication.provider}`);
      if (sec.authorization?.rbac) lines.push(`- RBAC: ${sec.authorization.rbac}`);
      if (sec.dataProtection) lines.push(`- Data Protection: Configured`);
    }
    if (cc.projectStructure) {
      const ps = cc.projectStructure;
      lines.push('\n### Project Structure');
      if (present(ps.directories || ps.folders)) lines.push(`- Directories: ${(ps.directories || ps.folders).length}`);
      if (present(ps.configFiles)) lines.push(`- Config Files: ${ps.configFiles.length}`);
      if (ps.packageManager) lines.push(`- Package Manager: ${ps.packageManager}`);
    }
    if (cc.deployment) {
      const dl = cc.deployment;
      lines.push('\n### Deployment');
      if (dl.hosting || dl.platform) lines.push(`- Platform: ${dl.hosting || dl.platform}`);
      if (dl.cicd || dl.pipeline) lines.push(`- CI/CD: Configured`);
      if (dl.monitoring) lines.push(`- Monitoring: Configured`);
    }
    if (cc.testing) {
      const ts = cc.testing;
      lines.push('\n### Testing');
      if (ts.unitTesting || ts.unitTests || ts.unit) lines.push(`- Unit Tests: Configured`);
      if (ts.e2eTesting || ts.e2eTests || ts.e2e) lines.push(`- E2E Tests: Configured`);
      if (ts.integrationTesting || ts.integrationTests || ts.integration) lines.push(`- Integration Tests: Configured`);
      if (ts.framework) lines.push(`- Framework: ${ts.framework}`);
    }

    return lines.join('\n');
  };

  const handleExportMarkdown = () => {
    if (!sanitizedBlueprint) return;
    const md = generateMarkdownSummary(sanitizedBlueprint);
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'technical-architecture.md';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Early returns for missing data
  // No analysis response
  if (!analysisResponse) {
    return (
      <Card className="p-4 sm:p-8 text-center">
        <Icon name="warning" size="2xl" className="mb-4 mx-auto text-[#FFB800]" />
        <Heading level={3} className="text-base sm:text-lg font-semibold mb-2">No Analysis Response</Heading>
        <Text variant="secondary">No analysis response provided to display technical architecture.</Text>
      </Card>
    );
  }

  // No technical architecture available
  if (!blueprint) {
    return (
      <div className="glass-panel p-4 sm:p-8 text-center rounded-2xl">
        <Icon name="x" size="2xl" className="mb-4 mx-auto text-[#FF6B6B]" />
        <Heading level={3} className="text-base sm:text-lg font-semibold mb-2 text-white">Complete Architecture Data Required</Heading>
        <Text variant="secondary" className="mb-4 text-gray-300">
          Technical blueprint is incomplete or missing required 4-layer architecture data.
        </Text>
        <Text variant="muted" size="sm" className="mb-2 text-gray-400">
          All 4 core layers must be present: Presentation, Business, Persistence, and Database.
        </Text>
        <Text variant="muted" size="sm" className="text-gray-400">
          Try regenerating the analysis or contact support if this issue persists.
        </Text>
      </div>
    );
  }

  // Toggle layer expansion
  const toggleLayer = (layerName) => {
    setExpandedLayers(prev => ({
      ...prev,
      [layerName]: !prev[layerName]
    }));
  };

  // Expand all layers
  const expandAllLayers = () => {
    setExpandedLayers({
      // 4 Core layers
      presentationLayer: true,
      businessLayer: true,
      persistenceLayer: true,
      databaseLayer: true,
      // 4 Cross-cutting concerns
      security: true,
      projectStructure: true,
      deployment: true,
      testing: true
    });
  };

  // Collapse all layers
  const collapseAllLayers = () => {
    setExpandedLayers({});
  };

  // Handle copy functionality
  const handleCopy = async (text, buttonId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus(prev => ({ ...prev, [buttonId]: 'success' }));
      setTimeout(() => {
        setCopyStatus(prev => ({ ...prev, [buttonId]: null }));
      }, 2000);
    } catch (err) {
      setCopyStatus(prev => ({ ...prev, [buttonId]: 'error' }));
      setTimeout(() => {
        setCopyStatus(prev => ({ ...prev, [buttonId]: null }));
      }, 2000);
    }
  };

  // Early validation check - show clear error if blueprint is invalid
  if (!sanitizedBlueprint || Object.keys(sanitizedBlueprint).every(key => !sanitizedBlueprint[key])) {
    return (
      <div id={containerId} className="w-full space-y-4">
        <Heading level={2} className="text-xl sm:text-2xl flex items-center space-x-2">
          <Icon name="settings" size="sm" className="text-[#7B61FF]" />
          <span>Technical Architecture</span>
        </Heading>
        <Alert variant="warning" className="bg-yellow-500/10 border-yellow-500/30">
          <div className="flex items-start space-x-3">
            <span className="text-2xl">⚠️</span>
            <div>
              <h3 className="font-semibold text-yellow-500 mb-2">Technical Architecture Data Incomplete</h3>
              <p className="text-yellow-500/90 mb-3">
                The analysis response doesn't contain valid technical architecture data (cursorBlueprint).
                This may be due to:
              </p>
              <ul className="list-disc list-inside text-yellow-500/80 space-y-1 text-sm">
                <li>Model returned empty or invalid response structure</li>
                <li>Required fields (components, services, tables) are missing or empty</li>
                <li>Data validation failed during processing</li>
              </ul>
              <p className="text-yellow-500/90 mt-3 text-sm">
                Please regenerate the analysis to create proper technical architecture.
              </p>
            </div>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div id={containerId} className="w-full space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Heading level={2} className="text-xl sm:text-2xl flex items-center space-x-2">
            <Icon name="settings" size="sm" className="text-[#7B61FF]" />
            <span>Technical Architecture</span>
          </Heading>
          <Text variant="secondary" size="sm" className="sm:text-base mt-1">4-Layer Architecture + Cross-Cutting Concerns</Text>
        </div>
        {/* Quick actions */}
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={handleExportJSON} className="text-gray-400 hover:text-white hover:bg-white/10">
            <Icon name="download" size="xs" className="mr-1" /> JSON
          </Button>
          <Button variant="ghost" size="sm" onClick={handleExportMarkdown} className="text-gray-400 hover:text-white hover:bg-white/10">
            <Icon name="document" size="xs" className="mr-1" /> MD
          </Button>
        </div>
      </div>

      {/* Evolution-style timeline navigator */}
      <div className="glass-panel p-4 sm:p-6 rounded-2xl">
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center">
          <div className="flex items-center">
            <Icon name="layers" size="sm" className="mr-2 text-[#7B61FF]" />
            <Text weight="bold" size="lg" className="text-white">Architecture Layers</Text>
          </div>
          <div className="sm:ml-auto mt-3 sm:mt-0">
            <div className="flex gap-2">
              <Input
                placeholder="Search components..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-64 bg-black/20 border-white/10 text-white placeholder-gray-500 focus:border-[#7B61FF]/50"
              />
              <Button
                size="sm"
                variant={density === 'comfortable' ? 'secondary' : 'ghost'}
                onClick={() => setDensity('comfortable')}
                className={density === 'comfortable' ? "bg-white/10 text-white" : "text-gray-400 hover:text-white hover:bg-white/5"}
              >Comfort</Button>
              <Button
                size="sm"
                variant={density === 'compact' ? 'secondary' : 'ghost'}
                onClick={() => setDensity('compact')}
                className={density === 'compact' ? "bg-white/10 text-white" : "text-gray-400 hover:text-white hover:bg-white/5"}
              >Compact</Button>
            </div>
          </div>
        </div>

        {(() => {
          // Helper to get data for core layers vs cross-cutting concerns
          const getLayerData = (key, isCrossCutting) => {
            if (isCrossCutting) {
              return sanitizedBlueprint?.crossCuttingConcerns?.[key] || {};
            }
            return sanitizedBlueprint?.[key] || {};
          };

          const calcSubtitle = (key, isCrossCutting = false) => {
            const c = getLayerData(key, isCrossCutting);
            switch (key) {
              // 4 Core layers
              case 'presentationLayer': {
                const comps = c.detailedComponents?.count || c.components?.length || 0;
                const screens = (c.screens || []).length;
                const framework = c.framework || '';
                if (comps || screens) return `${comps || 0} components · ${screens || 0} screens`;
                if (framework) return framework;
                return 'UI framework & screens';
              }
              case 'businessLayer': {
                const endpoints = c.detailedEndpoints?.count || c.endpoints?.length || 0;
                const entities = (c.domainEntities || []).length;
                const services = (c.services || []).length;
                if (entities || endpoints || services) return `${entities || 0} entities · ${endpoints || services || 0} endpoints`;
                return 'Domain logic & entities';
              }
              case 'persistenceLayer': {
                const patterns = (c.dataAccess?.patterns || []).length;
                const repos = (c.repositories || []).length;
                const orm = c.dataAccess?.orm ? 'ORM' : '';
                if (repos) return `${repos} repositories`;
                if (patterns || orm) return `${orm || 'Data access'} · ${patterns} patterns`;
                return 'Data access & caching';
              }
              case 'databaseLayer': {
                const tables = c.schema?.tables?.length || 0;
                const rels = c.schema?.relationships?.length || 0;
                if (tables || rels) return `${tables} tables · ${rels} relations`;
                return 'Schema & performance';
              }
              // 4 Cross-cutting concerns
              case 'security': {
                const hasAuth = !!(c.authentication?.strategy || c.authentication?.provider);
                const hasRBAC = !!c.authorization?.rbac;
                const authProvider = c.authentication?.provider || 'Auth';
                if (hasAuth || hasRBAC) return `${hasAuth ? authProvider : ''}${hasAuth && hasRBAC ? ' · ' : ''}${hasRBAC ? 'RBAC' : ''}`;
                return 'Auth, authorization & protection';
              }
              case 'projectStructure': {
                const folders = (c.directories || c.folders || []).length;
                const configStr = c.configuration;
                const configCount = configStr ? (typeof configStr === 'string' ? configStr.split(',').length : 0) : 0;
                if (folders || configCount) return `${folders} folders · ${configCount} configs`;
                return 'Project organization';
              }
              case 'deployment': {
                const platform = c.platform || c.hosting || '';
                const cicd = c.cicd || c.pipeline ? 'CI/CD' : '';
                if (platform || cicd) return `${platform}${platform && cicd ? ' · ' : ''}${cicd}`;
                return 'Hosting & deployment';
              }
              case 'testing': {
                const unit = c.unitTesting || c.unitTests || c.unit ? 'Unit' : '';
                const e2e = c.e2eTesting || c.e2eTests || c.e2e || c.integration ? 'E2E' : '';
                if (unit || e2e) return `${unit}${unit && e2e ? ' · ' : ''}${e2e}`;
                return 'Testing strategy';
              }
              default:
                return '';
            }
          };

          // Check if a step has data
          const hasStepData = (key, isCrossCutting) => {
            if (isCrossCutting) {
              return !!sanitizedBlueprint?.crossCuttingConcerns?.[key];
            }
            return !!sanitizedBlueprint?.[key];
          };

          const allSteps = [
            // 4 Core layers
            { key: 'presentationLayer', title: 'Presentation', icon: 'eye', section: 'core' },
            { key: 'businessLayer', title: 'Business', icon: 'settings', section: 'core' },
            { key: 'persistenceLayer', title: 'Persistence', icon: 'database', section: 'core' },
            { key: 'databaseLayer', title: 'Database', icon: 'server', section: 'core' },
            // 4 Cross-cutting concerns
            { key: 'security', title: 'Security', icon: 'lock', section: 'cross-cutting' },
            { key: 'projectStructure', title: 'Project Structure', icon: 'folder', section: 'cross-cutting' },
            { key: 'deployment', title: 'Deployment', icon: 'cloud', section: 'cross-cutting' },
            { key: 'testing', title: 'Testing', icon: 'check', section: 'cross-cutting' },
          ].filter(s => hasStepData(s.key, s.section === 'cross-cutting')).map((s, idx) => ({
            ...s,
            index: idx + 1,
            subtitle: calcSubtitle(s.key, s.section === 'cross-cutting')
          }));

          // Mobile vertical timeline
          const Mobile = (
            <div className="sm:hidden space-y-3">
              {allSteps.map((step, idx) => (
                <button
                  key={step.key}
                  onClick={() => setSelectedLayerKey(step.key)}
                  className={`w-full border rounded-xl p-3 flex items-center transition-all ${selectedLayerKey === step.key ? 'border-white/20 bg-white/10 shadow-lg shadow-black/20' : 'border-white/5 bg-transparent hover:bg-white/5'
                    }`}
                  aria-label={`Open ${step.title} layer`}
                >
                  <div className={`flex items-center justify-center rounded-full mr-3 ${getLayerAccent(step.key).bg} ${getLayerAccent(step.key).border} border backdrop-blur-sm`} style={{ width: 44, height: 44 }}>
                    <Icon name={step.icon} size="lg" className={getLayerAccent(step.key).icon} />
                  </div>
                  <div className="text-left">
                    <Text variant="primary" weight="medium" size="sm" className="text-white">{step.index}. {step.title}</Text>
                    {step.subtitle && (
                      <Text variant="secondary" size="xs" className="mt-0.5 text-gray-400">{step.subtitle}</Text>
                    )}
                  </div>
                </button>
              ))}
            </div>
          );

          // Desktop horizontal timeline
          const Desktop = (
            <div className="hidden sm:grid grid-cols-5 gap-3">
              {allSteps.map((step, index) => (
                <button
                  key={step.key}
                  onClick={() => setSelectedLayerKey(step.key)}
                  className={`border rounded-xl p-4 flex items-center transition-all text-left group ${selectedLayerKey === step.key ? 'border-white/20 bg-white/10 shadow-lg shadow-black/20' : 'border-white/5 bg-transparent hover:bg-white/5'
                    }`}
                  aria-label={`Open ${step.title} layer`}
                >
                  <div className={`flex items-center justify-center rounded-full mr-3 ${getLayerAccent(step.key).bg} ${getLayerAccent(step.key).border} border backdrop-blur-sm group-hover:scale-110 transition-transform`} style={{ width: 48, height: 48 }}>
                    <Icon name={step.icon} size="lg" className={getLayerAccent(step.key).icon} />
                  </div>
                  <div className="min-w-0">
                    <Text variant="primary" weight="medium" size="sm" className="text-white">{step.index}. {step.title}</Text>
                    {step.subtitle && (
                      <Text variant="secondary" size="xs" className="truncate text-gray-400">{step.subtitle}</Text>
                    )}
                  </div>
                </button>
              ))}
            </div>
          );

          return (
            <>
              {Mobile}
              {Desktop}
            </>
          );
        })()}
      </div>

      {/* Active layer content */}
      {selectedLayerKey ? (
        <div className="glass-panel p-4 sm:p-6 rounded-2xl">
          <Heading level={3} className="text-lg mb-4 text-white flex items-center">
            <span className={`w-2 h-8 rounded-full mr-3 ${getLayerAccent(selectedLayerKey).bg.replace('/10', '')} ${getLayerAccent(selectedLayerKey).icon.replace('text-', 'bg-')}`}></span>
            {/* 4 Core layers */}
            {selectedLayerKey === 'presentationLayer' && 'Presentation Layer'}
            {selectedLayerKey === 'businessLayer' && 'Business Layer'}
            {selectedLayerKey === 'persistenceLayer' && 'Persistence Layer'}
            {selectedLayerKey === 'databaseLayer' && 'Database Layer'}
            {/* 4 Cross-cutting concerns */}
            {selectedLayerKey === 'security' && 'Security'}
            {selectedLayerKey === 'projectStructure' && 'Project Structure'}
            {selectedLayerKey === 'deployment' && 'Deployment'}
            {selectedLayerKey === 'testing' && 'Testing'}
          </Heading>
          <div className="border-t border-white/5 pt-4">
            {(() => {
              const pad = density === 'compact' ? 'p-3' : 'p-4';
              const filterText = (searchQuery || '').toLowerCase();

              // Get content from correct location (core layers vs cross-cutting concerns)
              const isCrossCutting = ['security', 'projectStructure', 'deployment', 'testing'].includes(selectedLayerKey);
              const content = isCrossCutting
                ? sanitizedBlueprint?.crossCuttingConcerns?.[selectedLayerKey] || {}
                : sanitizedBlueprint?.[selectedLayerKey] || {};

              const layerRenderer = (key) => {
                switch (key) {
                  // 4 Core layers
                  case 'presentationLayer':
                    return formatPresentationLayer({ ...content }, { filterText, density, pad });
                  case 'businessLayer':
                    return formatBusinessLayer({ ...content }, { filterText, density, pad });
                  case 'persistenceLayer':
                    return formatPersistenceLayer({ ...content }, { filterText, density, pad });
                  case 'databaseLayer':
                    return formatDatabaseLayer({ ...content }, { filterText, density, pad });
                  // 4 Cross-cutting concerns
                  case 'security':
                    return formatSecurityLayer({ ...content }, { filterText, density, pad });
                  case 'projectStructure':
                    return formatProjectStructure({ ...content }, { filterText, density, pad });
                  case 'deployment':
                    return formatDeploymentLayer({ ...content }, { filterText, density, pad });
                  case 'testing':
                    return formatTestingStrategy({ ...content }, { filterText, density, pad });
                  default:
                    // Fallback: show JSON for unknown layers
                    return (
                      <pre className="whitespace-pre-wrap text-sm text-gray-300 font-mono overflow-x-auto bg-black/40 p-4 rounded-lg border border-white/5">
                        {JSON.stringify(content, null, 2)}
                      </pre>
                    );
                }
              };

              return layerRenderer(selectedLayerKey);
            })()}
          </div>
        </div>
      ) : (
        <div className="glass-panel p-6 text-center rounded-2xl">
          <Icon name="mouse" size="lg" className="mx-auto mb-2 text-gray-500" />
          <Text variant="secondary" className="text-gray-400">Select a layer above to view architectural details.</Text>
        </div>
      )}
    </div>
  );
};

// Utility: text match for filtering
const textMatches = (value, filterText) => {
  if (!filterText) return true;
  const v = (Array.isArray(value) ? value.join(' ') : value || '').toString().toLowerCase();
  return v.includes(filterText);
};

// Safely render inline values that might be objects/arrays
const inline = (value) => {
  if (value === null || value === undefined) return '';
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch (_err) {
    return String(value);
  }
};

// Professional formatting functions for each layer
const formatPresentationLayer = (content, opts = {}) => {
  const { filterText = '', density = 'comfortable' } = opts;
  const padClass = density === 'compact' ? 'p-3' : 'p-4';
  const gapClass = density === 'compact' ? 'gap-2' : 'gap-3';

  // Handle both old format (detailedComponents.list) and new format (components array)
  const rawComponents = content.detailedComponents?.list || content.components || [];
  const filteredComponents = rawComponents
    .filter((comp) => {
      // Skip null/undefined components
      if (!comp || typeof comp !== 'object') {
        return false;
      }

      // Validate component.name is a string with minimum length
      if (!comp.name || typeof comp.name !== 'string' || comp.name.length < 2) {
        return false;
      }

      // Validate component.purpose if present
      if (comp.purpose !== undefined && typeof comp.purpose !== 'string') {
        return false;
      }

      return true;
    })
    .filter((comp) => (
      textMatches(
        [comp.name, comp.purpose, comp.file, comp.props].filter(Boolean).join(' '),
        filterText.toLowerCase()
      )
    ));
  // Handle both old format (scr.components) and new format (scr.componentNames)
  const filteredScreens = (content.screens || []).filter((scr) => (
    textMatches(
      [scr.name, scr.purpose, scr.route, (scr.componentNames || scr.components || []).join(' ')].join(' '),
      filterText.toLowerCase()
    )
  ));
  return (
    <div className="space-y-6">
      {/* Framework & Technology */}
      {content.framework && (
        <div className={`bg-white/5 ${padClass} rounded-xl border border-white/5`}>
          <h4 className="font-semibold text-white mb-2 flex items-center">
            <Icon name="lightning" size="xs" className="text-[#7B61FF] mr-2" />
            Framework & Technology
          </h4>
          <p className="text-gray-300 mb-2">{content.framework}</p>
          {content.styling && <p className="text-sm text-gray-400"><strong>Styling:</strong> {inline(content.styling)}</p>}
          {content.stateManagement && (
            typeof content.stateManagement === 'string'
              ? <p className="text-sm text-gray-400"><strong>State Management:</strong> {inline(content.stateManagement)}</p>
              : <div className="mt-2 space-y-1">
                <p className="text-sm text-gray-400"><strong>State Management:</strong> {inline(content.stateManagement.pattern)}</p>
                {content.stateManagement.implementation && <p className="text-xs text-gray-500">{inline(content.stateManagement.implementation)}</p>}
                {content.stateManagement.stateFlow && <p className="text-xs text-gray-500"><strong className="text-gray-400">Flow:</strong> {inline(content.stateManagement.stateFlow)}</p>}
              </div>
          )}
        </div>
      )}

      {/* Components */}
      {/* Components */}
      {filteredComponents.length > 0 && (
        <div className={`bg-white/5 ${padClass} rounded-xl border border-white/5`}>
          <h4 className="font-semibold text-white mb-3 flex items-center">
            <Icon name="settings" size="xs" className="text-[#7B61FF] mr-2" />
            Components ({filteredComponents.length})
          </h4>
          <div className={`grid ${gapClass}`}>
            {filteredComponents.map((comp, index) => (
              <div key={index} className={`bg-black/20 ${density === 'compact' ? 'p-2' : 'p-3'} rounded-lg border border-white/5 hover:bg-white/5 transition-colors`}>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-1 gap-1">
                  <h5 className="font-medium text-white">{comp.name}</h5>
                  {comp.file && <code className="text-xs text-gray-400 px-2 py-0.5 rounded border border-white/5 bg-black/40 break-all whitespace-pre-wrap">{comp.file}</code>}
                </div>
                {comp.purpose && <p className="text-sm text-gray-400 mb-1">{comp.purpose}</p>}
                {comp.props && (
                  <div className="text-xs text-gray-400">
                    <strong>Props:</strong> <code className="px-1 rounded border border-white/5 bg-black/40 break-all whitespace-pre-wrap block mt-1">{inline(comp.props)}</code>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Project Structure */}
      {content.projectStructure?.folders?.length > 0 && (
        <div className={`bg-white/5 ${padClass} rounded-xl border border-white/5`}>
          <h4 className="font-semibold text-white mb-3 flex items-center">
            <Icon name="folder" size="xs" className="text-[#7B61FF] mr-2" />
            Project Structure
          </h4>
          <div className={`grid ${gapClass}`}>
            {content.projectStructure.folders.map((folder, index) => (
              <div key={index} className={`bg-black/20 ${density === 'compact' ? 'p-2' : 'p-3'} rounded-lg border border-white/5 hover:bg-white/5 transition-colors`}>
                <div className="flex items-center mb-1">
                  <Icon name="folder" size="xs" className="text-gray-400 mr-2" />
                  <code className="font-medium text-gray-300">{folder.name}</code>
                </div>
                <p className="text-sm text-gray-400 mb-2">{folder.purpose}</p>
                {folder.subfolders?.length > 0 && (
                  <div className="ml-4">
                    <p className="text-xs font-medium text-gray-400 mb-1">Subfolders:</p>
                    <div className="flex flex-wrap gap-1">
                      {folder.subfolders.map((sub, subIndex) => (
                        <code key={subIndex} className="text-xs text-gray-400 px-2 py-0.5 rounded border border-white/5 bg-black/40">{sub}</code>
                      ))}
                    </div>
                  </div>
                )}
                {folder.files?.length > 0 && (
                  <div className="ml-4 mt-2">
                    <p className="text-xs font-medium text-gray-400 mb-1">Key Files:</p>
                    <div className="flex flex-wrap gap-1">
                      {folder.files.map((file, fileIndex) => (
                        <code key={fileIndex} className="text-xs text-gray-400 px-2 py-0.5 rounded border border-white/5 bg-black/40">{file}</code>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {content.projectStructure.configuration && (
            <div className="mt-3 p-3 bg-black/20 border border-white/5 rounded-lg">
              <p className="text-sm text-gray-300"><strong>Configuration:</strong> {inline(content.projectStructure.configuration)}</p>
            </div>
          )}
        </div>
      )}

      {/* Screens */}
      {filteredScreens.length > 0 && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="phone" size="sm" className="mr-2 text-[#7B61FF]" />
            Screens & Routes ({filteredScreens.length})
          </Heading>
          <div className={`grid ${gapClass}`}>
            {filteredScreens.map((screen, index) => (
              <div key={index} className={`bg-black/20 ${density === 'compact' ? 'p-2' : 'p-3'} rounded-lg border border-white/5 hover:bg-white/5 transition-colors`}>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-1">
                  <Heading level={5} className="font-medium text-white">{screen.name}</Heading>
                  {screen.route && <code className="text-xs text-gray-300 px-2 py-0.5 rounded border border-white/5 bg-black/40 break-all">{screen.route}</code>}
                </div>
                {screen.purpose && <Text variant="secondary" size="sm" className="mb-2 text-gray-400">{screen.purpose}</Text>}
                {(screen.componentNames || screen.components) && (
                  <Text size="xs" className="text-gray-500">
                    <span className="font-bold text-gray-400">Components:</span> {(screen.componentNames || screen.components).join(', ')}
                  </Text>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Routing */}
      {content.routing && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="link" size="sm" className="mr-2 text-[#7B61FF]" />
            Routing & Navigation
          </Heading>
          <div className="space-y-2">
            {content.routing.pattern && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Pattern:</span> {inline(content.routing.pattern)}</Text>}
            {content.routing.implementation && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Implementation:</span> {inline(content.routing.implementation)}</Text>}
            {content.routing.guards && Array.isArray(content.routing.guards) && content.routing.guards.length > 0 && (
              <div className="mt-2">
                <Text variant="muted" size="xs" className="text-gray-500 mb-1">Route Guards:</Text>
                <div className="flex flex-wrap gap-1">
                  {content.routing.guards.map((guard, idx) => (
                    <span key={idx} className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded">{guard}</span>
                  ))}
                </div>
              </div>
            )}
            {content.routing.middleware && Array.isArray(content.routing.middleware) && content.routing.middleware.length > 0 && (
              <div className="mt-2">
                <Text variant="muted" size="xs" className="text-gray-500 mb-1">Middleware:</Text>
                <div className="flex flex-wrap gap-1">
                  {content.routing.middleware.map((mw, idx) => (
                    <span key={idx} className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded">{mw}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deployment */}
      {content.deployment && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="rocket" size="sm" className="mr-2 text-[#7B61FF]" />
            Deployment Configuration
          </Heading>
          <div className="space-y-2">
            {content.deployment.hosting && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Hosting:</span> {inline(content.deployment.hosting)}</Text>}
            {content.deployment.cdn && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">CDN:</span> {inline(content.deployment.cdn)}</Text>}
            {content.deployment.performance && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Performance:</span> {inline(content.deployment.performance)}</Text>}
          </div>
        </div>
      )}
    </div>
  );
};

// Format Business Layer - COMPLETE DATA
const formatBusinessLayer = (content, opts = {}) => {
  const { filterText = '', density = 'comfortable' } = opts;
  const padClass = density === 'compact' ? 'p-3' : 'p-4';
  const gapClass = density === 'compact' ? 'gap-2' : 'gap-3';

  // Support both detailedEndpoints.list and endpoints[] formats
  const endpointsList = content.detailedEndpoints?.list || content.endpoints || [];
  const filteredEndpoints = endpointsList.filter((e) => (
    textMatches(
      [e.method, e.route, e.path, e.purpose, e.file, (e.middleware || []).join(' '), e.parameters, e.response, e.authentication, e.validation].filter(Boolean).join(' '),
      filterText.toLowerCase()
    )
  ));

  return (
    <div className="space-y-6">
      {/* Architecture Overview */}
      {content.architecture && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="settings" size="sm" className="mr-2 text-green-400" />
            Architecture Pattern
          </Heading>
          <Text variant="secondary" className="text-gray-300">{inline(content.architecture)}</Text>
        </div>
      )}

      {/* Services - FULL DETAILS with methods, validation, processing */}
      {content.services?.length > 0 && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="settings" size="sm" className="mr-2 text-green-400" />
            Services ({content.services.length})
          </Heading>
          <div className={`grid ${gapClass}`}>
            {content.services.map((service, index) => (
              <div key={index} className={`bg-black/20 ${density === 'compact' ? 'p-2' : 'p-3'} rounded-lg border border-white/5`}>
                <Text weight="medium" className="text-white">{typeof service === 'string' ? service : service.name}</Text>
                {service.purpose && <Text variant="secondary" size="sm" className="text-gray-400 mt-1">{service.purpose}</Text>}
                {service.methods?.length > 0 && (
                  <div className="mt-2">
                    <Text variant="muted" size="xs" className="text-gray-500 mb-1">Methods:</Text>
                    <div className="flex flex-wrap gap-1">
                      {service.methods.map((method, mIdx) => (
                        <Badge key={mIdx} variant="secondary" size="xs" className="bg-green-500/10 text-green-400 border-green-500/20">{method}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {service.validation && (
                  <Text size="xs" className="text-gray-500 mt-2"><span className="font-medium">Validation:</span> {service.validation}</Text>
                )}
                {service.processing && (
                  <Text size="xs" className="text-gray-500 mt-1"><span className="font-medium">Processing:</span> {service.processing}</Text>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* API Endpoints - FULL DETAILS */}
      {filteredEndpoints.length > 0 && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="arrowRight" size="sm" className="mr-2 text-green-400" />
            API Endpoints ({filteredEndpoints.length})
          </Heading>
          <div className={`grid ${gapClass}`}>
            {filteredEndpoints.map((endpoint, index) => (
              <div key={index} className={`bg-black/20 ${density === 'compact' ? 'p-2' : 'p-3'} rounded-lg border border-white/5`}>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-1">
                  <div className="flex items-center space-x-2 min-w-0">
                    <Badge variant="secondary" size="xs" className="bg-green-500/20 text-green-400 shrink-0">{endpoint.method}</Badge>
                    <code className="font-medium text-gray-300 break-all">{endpoint.route || endpoint.path}</code>
                  </div>
                  {endpoint.file && <code className="text-xs text-green-400/70 px-2 py-0.5 rounded border border-white/5 bg-black/40 break-all mt-1 sm:mt-0">{endpoint.file}</code>}
                </div>
                {endpoint.purpose && <Text variant="secondary" size="sm" className="mb-2 text-gray-400">{endpoint.purpose}</Text>}
                {endpoint.authentication && (
                  <Text size="xs" className="text-gray-500"><span className="font-medium">Auth:</span> {endpoint.authentication}</Text>
                )}
                {endpoint.validation && (
                  <Text size="xs" className="text-gray-500 mt-1"><span className="font-medium">Validation:</span> {endpoint.validation}</Text>
                )}
                {endpoint.response && (
                  <Text size="xs" className="text-gray-500 mt-1"><span className="font-medium">Response:</span> {endpoint.response}</Text>
                )}
                {endpoint.middleware?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {endpoint.middleware.map((mw, mwIndex) => (
                      <Badge key={mwIndex} variant="secondary" size="xs" className="bg-white/10 text-gray-400">{mw}</Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Event Handling */}
      {content.eventHandling && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="lightning" size="sm" className="mr-2 text-green-400" />
            Event Handling
          </Heading>
          <div className="space-y-3">
            {content.eventHandling.pattern && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Pattern:</span> {inline(content.eventHandling.pattern)}</Text>}
            {content.eventHandling.events?.length > 0 && (
              <div>
                <Text variant="muted" size="xs" className="text-gray-500 mb-1">Events:</Text>
                <div className="flex flex-wrap gap-1">
                  {content.eventHandling.events.map((evt, idx) => (
                    <Badge key={idx} variant="secondary" size="xs" className="bg-green-500/10 text-green-400">{evt}</Badge>
                  ))}
                </div>
              </div>
            )}
            {content.eventHandling.handlers?.length > 0 && (
              <div>
                <Text variant="muted" size="xs" className="text-gray-500 mb-1">Handlers:</Text>
                <div className="flex flex-wrap gap-1">
                  {content.eventHandling.handlers.map((handler, idx) => (
                    <Badge key={idx} variant="secondary" size="xs" className="bg-white/10 text-gray-400">{handler}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Business Rules */}
      {content.businessRules && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="shield" size="sm" className="mr-2 text-green-400" />
            Business Rules
          </Heading>
          <div className="space-y-3">
            {content.businessRules.validation && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Validation:</span> {inline(content.businessRules.validation)}</Text>}
            {content.businessRules.constraints && (
              <div>
                <Text variant="muted" size="xs" className="text-gray-500 mb-1">Constraints:</Text>
                <div className="grid gap-1">
                  {Array.isArray(content.businessRules.constraints)
                    ? content.businessRules.constraints.map((c, idx) => (
                      <Text key={idx} size="xs" className="text-gray-400">• {c}</Text>
                    ))
                    : <Text size="xs" className="text-gray-400">{content.businessRules.constraints}</Text>
                  }
                </div>
              </div>
            )}
            {content.businessRules.policies && (
              <div>
                <Text variant="muted" size="xs" className="text-gray-500 mb-1">Policies:</Text>
                <div className="grid gap-1">
                  {Array.isArray(content.businessRules.policies)
                    ? content.businessRules.policies.map((p, idx) => (
                      <Text key={idx} size="xs" className="text-gray-400">• {p}</Text>
                    ))
                    : <Text size="xs" className="text-gray-400">{content.businessRules.policies}</Text>
                  }
                </div>
              </div>
            )}
            {content.businessRules.workflows && (
              <div>
                <Text variant="muted" size="xs" className="text-gray-500 mb-1">Workflows:</Text>
                <div className="grid gap-1">
                  {Array.isArray(content.businessRules.workflows)
                    ? content.businessRules.workflows.map((w, idx) => (
                      <Text key={idx} size="xs" className="text-gray-400">• {w}</Text>
                    ))
                    : <Text size="xs" className="text-gray-400">{content.businessRules.workflows}</Text>
                  }
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Domain Entities */}
      {content.domainEntities?.length > 0 && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="folder" size="sm" className="mr-2 text-green-400" />
            Domain Entities ({content.domainEntities.length})
          </Heading>
          <div className="flex flex-wrap gap-2">
            {content.domainEntities.map((entity, idx) => (
              <Badge key={idx} variant="secondary" className="bg-green-500/10 text-green-400 border-green-500/20">
                {typeof entity === 'string' ? entity : entity.name}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Format Persistence Layer - COMPLETE DATA
const formatPersistenceLayer = (content, opts = {}) => {
  const { density = 'comfortable' } = opts;
  const padClass = density === 'compact' ? 'p-3' : 'p-4';
  const gapClass = density === 'compact' ? 'gap-2' : 'gap-3';

  return (
    <div className="space-y-6">
      {/* Pattern Overview */}
      {content.pattern && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="settings" size="sm" className="mr-2 text-[#FFB800]" />
            Persistence Pattern
          </Heading>
          <Text variant="secondary" className="text-gray-300">{inline(content.pattern)}</Text>
        </div>
      )}

      {/* Repositories - FULL DETAILS */}
      {content.repositories?.length > 0 && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="folder" size="sm" className="mr-2 text-[#FFB800]" />
            Repositories ({content.repositories.length})
          </Heading>
          <div className={`grid ${gapClass}`}>
            {content.repositories.map((repo, index) => (
              <div key={index} className={`bg-black/20 ${density === 'compact' ? 'p-2' : 'p-3'} rounded-lg border border-white/5`}>
                <div className="flex items-center gap-2 mb-1">
                  <Text weight="medium" className="text-white">{repo.name}</Text>
                  {repo.entity && <Badge variant="secondary" size="xs" className="bg-[#FFB800]/10 text-[#FFB800]">{repo.entity}</Badge>}
                </div>
                {repo.purpose && <Text variant="secondary" size="sm" className="text-gray-400">{repo.purpose}</Text>}
                {repo.methods?.length > 0 && (
                  <div className="mt-2">
                    <Text variant="muted" size="xs" className="text-gray-500 mb-1">Methods:</Text>
                    <div className="flex flex-wrap gap-1">
                      {repo.methods.map((method, mIdx) => (
                        <Badge key={mIdx} variant="secondary" size="xs" className="bg-white/5 text-gray-400">{method}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data Access - FULL DETAILS */}
      {content.dataAccess && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="refresh" size="sm" className="mr-2 text-[#FFB800]" />
            Data Access
          </Heading>
          <div className="space-y-2">
            {content.dataAccess.orm && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">ORM:</span> {inline(content.dataAccess.orm)}</Text>}
            {content.dataAccess.connectionManagement && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Connection Management:</span> {inline(content.dataAccess.connectionManagement)}</Text>}
            {content.dataAccess.transactionHandling && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Transaction Handling:</span> {inline(content.dataAccess.transactionHandling)}</Text>}
            {content.dataAccess.errorHandling && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Error Handling:</span> {inline(content.dataAccess.errorHandling)}</Text>}
            {content.dataAccess.patterns?.length > 0 && (
              <div className="mt-2">
                <Text variant="muted" size="xs" className="text-gray-500 mb-1">Patterns:</Text>
                <div className="flex flex-wrap gap-1">
                  {content.dataAccess.patterns.map((pattern, index) => (
                    <Badge key={index} variant="secondary" size="xs" className="bg-[#FFB800]/10 text-[#FFB800]">{pattern}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Caching - FULL DETAILS */}
      {content.caching && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="lightning" size="sm" className="mr-2 text-[#FFB800]" />
            Caching Strategy
          </Heading>
          <div className="space-y-2">
            {content.caching.strategy && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Strategy:</span> {inline(content.caching.strategy)}</Text>}
            {content.caching.implementation && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Implementation:</span> {inline(content.caching.implementation)}</Text>}
            {content.caching.invalidation && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Invalidation:</span> {inline(content.caching.invalidation)}</Text>}
          </div>
        </div>
      )}
    </div>
  );
};

// Format Database Layer - COMPLETE DATA
const formatDatabaseLayer = (content, opts = {}) => {
  const { filterText = '', density = 'comfortable' } = opts;
  const padClass = density === 'compact' ? 'p-3' : 'p-4';
  const gapClass = density === 'compact' ? 'gap-2' : 'gap-3';
  const filteredTables = (content.schema?.tables || []).filter((t) => (
    textMatches(
      [t.name, t.purpose, (t.fields || []).join(' '), (t.indexes || []).join(' '), (t.constraints || []).join(' ')].join(' '),
      filterText.toLowerCase()
    )
  ));

  return (
    <div className="space-y-6">
      {/* Database Info */}
      {content.database && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="database" size="sm" className="mr-2 text-sky-400" />
            Database Configuration
          </Heading>
          <div className="space-y-2">
            {content.database.type && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Type:</span> {content.database.type}</Text>}
            {content.database.version && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Version:</span> {content.database.version}</Text>}
            {content.database.hosting && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Hosting:</span> {content.database.hosting}</Text>}
          </div>
        </div>
      )}

      {/* Schema - Tables */}
      {filteredTables.length > 0 && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="chart" size="sm" className="mr-2 text-sky-400" />
            Tables ({filteredTables.length})
          </Heading>
          {content.schema?.description && (
            <Text variant="secondary" className="mb-4 text-gray-300">{content.schema.description}</Text>
          )}
          <div className={`grid ${gapClass}`}>
            {filteredTables.map((table, index) => (
              <div key={index} className={`bg-black/20 ${density === 'compact' ? 'p-2' : 'p-3'} rounded-lg border border-white/5`}>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-1">
                  <Text weight="medium" className="text-white break-all">{table.name}</Text>
                  <Badge variant="secondary" size="xs" className="bg-sky-500/10 text-sky-400 shrink-0">Table</Badge>
                </div>
                {table.purpose && <Text variant="secondary" size="sm" className="mb-3 text-gray-400">{table.purpose}</Text>}
                {table.fields?.length > 0 && (
                  <div className="mb-3">
                    <Text variant="muted" size="xs" className="mb-2 text-gray-500">Fields ({table.fields.length}):</Text>
                    <div className="grid gap-1 max-h-48 overflow-y-auto">
                      {table.fields.map((field, fieldIndex) => (
                        <code key={fieldIndex} className="text-xs text-gray-400 px-2 py-1 rounded block border border-white/5 bg-black/40 font-mono break-all whitespace-pre-wrap">
                          {field}
                        </code>
                      ))}
                    </div>
                  </div>
                )}
                {table.indexes?.length > 0 && (
                  <div className="mb-2">
                    <Text variant="muted" size="xs" className="mb-1 text-gray-500">Indexes:</Text>
                    <div className="grid gap-1">
                      {table.indexes.map((idx, idxIndex) => (
                        <code key={idxIndex} className="text-xs text-sky-400/70 bg-black/40 px-2 py-1 rounded font-mono">{idx}</code>
                      ))}
                    </div>
                  </div>
                )}
                {table.constraints?.length > 0 && (
                  <div>
                    <Text variant="muted" size="xs" className="mb-1 text-gray-500">Constraints:</Text>
                    <div className="grid gap-1">
                      {table.constraints.map((constraint, cIdx) => (
                        <code key={cIdx} className="text-xs text-gray-400 bg-black/40 px-2 py-1 rounded font-mono">{constraint}</code>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Relationships */}
      {content.schema?.relationships?.length > 0 && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="link" size="sm" className="mr-2 text-sky-400" />
            Relationships ({content.schema.relationships.length})
          </Heading>
          <div className="grid gap-2">
            {content.schema.relationships.map((rel, index) => (
              <Text key={index} size="sm" className="border border-white/5 px-3 py-2 rounded-lg text-gray-300 bg-black/20">• {rel}</Text>
            ))}
          </div>
        </div>
      )}

      {/* Views */}
      {content.schema?.views?.length > 0 && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="eye" size="sm" className="mr-2 text-sky-400" />
            Database Views ({content.schema.views.length})
          </Heading>
          <div className="grid gap-2">
            {content.schema.views.map((view, index) => (
              <code key={index} className="text-xs text-gray-400 bg-black/40 px-3 py-2 rounded-lg font-mono overflow-x-auto block">{view}</code>
            ))}
          </div>
        </div>
      )}

      {/* Performance */}
      {content.performance && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="lightning" size="sm" className="mr-2 text-sky-400" />
            Performance Optimization
          </Heading>
          <div className="space-y-2">
            {content.performance.indexing && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Indexing:</span> {inline(content.performance.indexing)}</Text>}
            {content.performance.queryOptimization && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Query Optimization:</span> {inline(content.performance.queryOptimization)}</Text>}
            {content.performance.monitoring && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Monitoring:</span> {inline(content.performance.monitoring)}</Text>}
            {content.performance.maintenance && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Maintenance:</span> {inline(content.performance.maintenance)}</Text>}
          </div>
        </div>
      )}

      {/* Migrations */}
      {content.migrations && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="refresh" size="sm" className="mr-2 text-sky-400" />
            Migrations
          </Heading>
          <div className="space-y-2">
            {content.migrations.strategy && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Strategy:</span> {inline(content.migrations.strategy)}</Text>}
            {content.migrations.versioning && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Versioning:</span> {inline(content.migrations.versioning)}</Text>}
            {content.migrations.rollback && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Rollback:</span> {inline(content.migrations.rollback)}</Text>}
          </div>
        </div>
      )}

      {/* Backup */}
      {content.backup && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="cloud" size="sm" className="mr-2 text-sky-400" />
            Backup Strategy
          </Heading>
          <div className="space-y-2">
            {content.backup.strategy && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Strategy:</span> {inline(content.backup.strategy)}</Text>}
            {content.backup.retention && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Retention:</span> {inline(content.backup.retention)}</Text>}
            {content.backup.recovery && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Recovery:</span> {inline(content.backup.recovery)}</Text>}
          </div>
        </div>
      )}
    </div>
  );
};

const formatSecurityLayer = (content, opts = {}) => {
  const { density = 'comfortable' } = opts;
  const padClass = density === 'compact' ? 'p-3' : 'p-4';
  const gapClass = density === 'compact' ? 'gap-2' : 'gap-3';
  return (
    <div className="space-y-6">
      {/* Authentication - COMPLETE DATA */}
      {content.authentication && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="lock" size="sm" className="mr-2 text-[#7B61FF]" />
            Authentication
          </Heading>
          <div className="space-y-3">
            {(content.authentication.strategy || content.authentication.provider) && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Provider:</span> {inline(content.authentication.strategy || content.authentication.provider)}</Text>}
            {content.authentication.providers && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Providers:</span> {inline(content.authentication.providers)}</Text>}
            {content.authentication.flow && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Flow:</span> {inline(content.authentication.flow)}</Text>}
            {content.authentication.implementation && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Implementation:</span> {inline(content.authentication.implementation)}</Text>}
            {/* Token Management */}
            {content.authentication.tokenManagement && (
              <div className="mt-2 pl-4 border-l-2 border-[#7B61FF]/30">
                <Text size="sm" className="font-semibold text-gray-200 mb-1">Token Management</Text>
                {content.authentication.tokenManagement.type && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Type:</span> {inline(content.authentication.tokenManagement.type)}</Text>}
                {content.authentication.tokenManagement.storage && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Storage:</span> {inline(content.authentication.tokenManagement.storage)}</Text>}
                {content.authentication.tokenManagement.refresh && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Refresh:</span> {inline(content.authentication.tokenManagement.refresh)}</Text>}
                {content.authentication.tokenManagement.expiration && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Expiration:</span> {inline(content.authentication.tokenManagement.expiration)}</Text>}
              </div>
            )}
            {/* Session Handling */}
            {(content.authentication.session || content.authentication.sessionHandling) && (
              <div className="mt-2 pl-4 border-l-2 border-[#7B61FF]/30">
                <Text size="sm" className="font-semibold text-gray-200 mb-1">Session Handling</Text>
                {(() => {
                  const session = content.authentication.sessionHandling || content.authentication.session;
                  if (typeof session === 'string') return <Text size="xs" className="text-gray-400">{session}</Text>;
                  return (
                    <>
                      {session.storage && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Storage:</span> {inline(session.storage)}</Text>}
                      {session.duration && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Duration:</span> {inline(session.duration)}</Text>}
                      {session.renewal && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Renewal:</span> {inline(session.renewal)}</Text>}
                      {session.invalidation && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Invalidation:</span> {inline(session.invalidation)}</Text>}
                    </>
                  );
                })()}
              </div>
            )}
            {content.authentication.middleware && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Middleware:</span> {inline(content.authentication.middleware)}</Text>}
            {content.authentication.mfa && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">MFA:</span> {inline(content.authentication.mfa)}</Text>}
          </div>
        </div>
      )}

      {/* Authorization - COMPLETE DATA */}
      {content.authorization && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="shield" size="sm" className="mr-2 text-[#7B61FF]" />
            Authorization
          </Heading>
          <div className="space-y-3">
            {content.authorization.strategy && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Strategy:</span> {inline(content.authorization.strategy)}</Text>}
            {/* RBAC Details */}
            {content.authorization.rbac && (
              <div className="mt-2 pl-4 border-l-2 border-[#7B61FF]/30">
                <Text size="sm" className="font-semibold text-gray-200 mb-2">Role-Based Access Control</Text>
                {typeof content.authorization.rbac === 'string' ? (
                  <Text size="xs" className="text-gray-400">{content.authorization.rbac}</Text>
                ) : (
                  <>
                    {content.authorization.rbac.roles && (
                      <div className="mb-2">
                        <Text size="xs" className="text-gray-300 font-medium">Roles:</Text>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Array.isArray(content.authorization.rbac.roles)
                            ? content.authorization.rbac.roles.map((role, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">{typeof role === 'string' ? role : role.name}</span>
                            ))
                            : <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">{content.authorization.rbac.roles}</span>
                          }
                        </div>
                      </div>
                    )}
                    {content.authorization.rbac.permissions && (
                      <div className="mb-2">
                        <Text size="xs" className="text-gray-300 font-medium">Permissions:</Text>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Array.isArray(content.authorization.rbac.permissions)
                            ? content.authorization.rbac.permissions.map((perm, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs">{typeof perm === 'string' ? perm : perm.name}</span>
                            ))
                            : <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs">{content.authorization.rbac.permissions}</span>
                          }
                        </div>
                      </div>
                    )}
                    {content.authorization.rbac.implementation && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Implementation:</span> {inline(content.authorization.rbac.implementation)}</Text>}
                  </>
                )}
              </div>
            )}
            {content.authorization.permissions && !content.authorization.rbac?.permissions && (
              <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Permissions:</span> {inline(content.authorization.permissions)}</Text>
            )}
            {/* Policies */}
            {content.authorization.policies && (
              <div className="mt-2">
                <Text size="sm" className="font-semibold text-gray-200 mb-2">Policies</Text>
                <div className={`grid ${gapClass}`}>
                  {Array.isArray(content.authorization.policies)
                    ? content.authorization.policies.map((policy, idx) => (
                      <div key={idx} className="bg-black/20 p-2 rounded-lg border border-white/5">
                        <Text size="xs" className="text-white font-medium">{typeof policy === 'string' ? policy : policy.name}</Text>
                        {policy.description && <Text size="xs" className="text-gray-400">{policy.description}</Text>}
                        {policy.rules && <Text size="xs" className="text-gray-500"><span className="text-gray-400">Rules:</span> {inline(policy.rules)}</Text>}
                      </div>
                    ))
                    : <div className="bg-black/20 p-2 rounded-lg border border-white/5">
                      <Text size="xs" className="text-white font-medium">{content.authorization.policies}</Text>
                    </div>
                  }
                </div>
              </div>
            )}
            {content.authorization.middleware && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Middleware:</span> {inline(content.authorization.middleware)}</Text>}
          </div>
        </div>
      )}

      {/* Data Protection - COMPLETE DATA */}
      {content.dataProtection && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="lock" size="sm" className="mr-2 text-[#7B61FF]" />
            Data Protection
          </Heading>
          <div className="space-y-3">
            {/* Encryption - Nested details */}
            {content.dataProtection.encryption && (
              <div className="pl-4 border-l-2 border-green-500/30">
                <Text size="sm" className="font-semibold text-gray-200 mb-1">Encryption</Text>
                {typeof content.dataProtection.encryption === 'string' ? (
                  <Text size="xs" className="text-gray-400">{content.dataProtection.encryption}</Text>
                ) : (
                  <>
                    {content.dataProtection.encryption.atRest && <Text size="xs" className="text-gray-400"><span className="text-gray-300">At Rest:</span> {inline(content.dataProtection.encryption.atRest)}</Text>}
                    {content.dataProtection.encryption.inTransit && <Text size="xs" className="text-gray-400"><span className="text-gray-300">In Transit:</span> {inline(content.dataProtection.encryption.inTransit)}</Text>}
                    {content.dataProtection.encryption.keys && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Key Management:</span> {inline(content.dataProtection.encryption.keys)}</Text>}
                    {content.dataProtection.encryption.algorithm && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Algorithm:</span> {inline(content.dataProtection.encryption.algorithm)}</Text>}
                    {content.dataProtection.encryption.implementation && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Implementation:</span> {inline(content.dataProtection.encryption.implementation)}</Text>}
                  </>
                )}
              </div>
            )}
            {/* Validation - Nested details */}
            {content.dataProtection.validation && (
              <div className="pl-4 border-l-2 border-blue-500/30">
                <Text size="sm" className="font-semibold text-gray-200 mb-1">Validation</Text>
                {typeof content.dataProtection.validation === 'string' ? (
                  <Text size="xs" className="text-gray-400">{content.dataProtection.validation}</Text>
                ) : (
                  <>
                    {content.dataProtection.validation.input && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Input:</span> {inline(content.dataProtection.validation.input)}</Text>}
                    {content.dataProtection.validation.output && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Output:</span> {inline(content.dataProtection.validation.output)}</Text>}
                    {content.dataProtection.validation.business && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Business:</span> {inline(content.dataProtection.validation.business)}</Text>}
                    {content.dataProtection.validation.schema && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Schema:</span> {inline(content.dataProtection.validation.schema)}</Text>}
                    {content.dataProtection.validation.implementation && <Text size="xs" className="text-gray-400"><span className="text-gray-300">Implementation:</span> {inline(content.dataProtection.validation.implementation)}</Text>}
                  </>
                )}
              </div>
            )}
            {content.dataProtection.sanitization && (
              <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Sanitization:</span> {inline(content.dataProtection.sanitization)}</Text>
            )}
            {content.dataProtection.privacy && (
              <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Privacy:</span> {inline(content.dataProtection.privacy)}</Text>
            )}
            {content.dataProtection.masking && (
              <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Data Masking:</span> {inline(content.dataProtection.masking)}</Text>
            )}
          </div>
        </div>
      )}

      {/* Compliance - COMPLETE DATA */}
      {content.compliance && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="check" size="sm" className="mr-2 text-green-400" />
            Compliance
          </Heading>
          <div className="space-y-2">
            {content.compliance.standards && (
              <div>
                <Text size="sm" className="font-semibold text-gray-200 mb-1">Standards</Text>
                <div className="flex flex-wrap gap-1">
                  {Array.isArray(content.compliance.standards)
                    ? content.compliance.standards.map((std, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-green-500/20 text-green-300 rounded text-xs">{typeof std === 'string' ? std : std.name}</span>
                    ))
                    : <span className="px-2 py-0.5 bg-green-500/20 text-green-300 rounded text-xs">{content.compliance.standards}</span>
                  }
                </div>
              </div>
            )}
            {content.compliance.regulations && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Regulations:</span> {inline(content.compliance.regulations)}</Text>}
            {content.compliance.implementation && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Implementation:</span> {inline(content.compliance.implementation)}</Text>}
            {content.compliance.auditing && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Auditing:</span> {inline(content.compliance.auditing)}</Text>}
            {content.compliance.reporting && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Reporting:</span> {inline(content.compliance.reporting)}</Text>}
          </div>
        </div>
      )}

      {/* Deployment Security */}
      {content.deployment && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="rocket" size="sm" className="mr-2 text-[#7B61FF]" />
            Deployment Security
          </Heading>
          <div className="space-y-2">
            {content.deployment.environment && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Environment:</span> {inline(content.deployment.environment)}</Text>}
            {content.deployment.secrets && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Secrets Management:</span> {inline(content.deployment.secrets)}</Text>}
            {content.deployment.monitoring && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Monitoring:</span> {inline(content.deployment.monitoring)}</Text>}
            {content.deployment.logging && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Logging:</span> {inline(content.deployment.logging)}</Text>}
            {content.deployment.compliance && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Compliance:</span> {inline(content.deployment.compliance)}</Text>}
          </div>
        </div>
      )}
    </div>
  );
};

// Format Project Structure layer
const formatProjectStructure = (content, opts = {}) => {
  const { density = 'comfortable' } = opts;
  const padClass = density === 'compact' ? 'p-3' : 'p-4';
  const gapClass = density === 'compact' ? 'gap-2' : 'gap-3';

  return (
    <div className="space-y-6">
      {/* Folders Structure */}
      {(content.folders?.length > 0 || content.directories?.length > 0) && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="folder" size="sm" className="mr-2 text-amber-400" />
            Directory Structure
          </Heading>
          <div className={`grid ${gapClass}`}>
            {(content.folders || content.directories || []).map((folder, index) => (
              <div key={index} className={`bg-black/20 ${density === 'compact' ? 'p-2' : 'p-3'} rounded-lg border border-white/5`}>
                <div className="flex items-start gap-2">
                  <Icon name="folder" size="xs" className="text-amber-400 mt-1" />
                  <div className="flex-1">
                    <Text className="font-medium text-white">{typeof folder === 'string' ? folder : folder.name}</Text>
                    {folder.purpose && <Text variant="secondary" size="sm" className="text-gray-400">{folder.purpose}</Text>}
                    {folder.subfolders?.length > 0 && (
                      <div className="mt-2 pl-4 border-l border-white/10">
                        {folder.subfolders.map((sub, subIdx) => (
                          <Text key={subIdx} size="xs" className="text-gray-500">{sub}</Text>
                        ))}
                      </div>
                    )}
                    {folder.files?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {folder.files.map((file, fileIdx) => (
                          <Badge key={fileIdx} variant="secondary" size="xs" className="bg-white/5 text-gray-400 border-white/5">{file}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Configuration */}
      {content.configuration && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="settings" size="sm" className="mr-2 text-amber-400" />
            Configuration
          </Heading>
          <Text variant="secondary" size="sm" className="text-gray-300 whitespace-pre-wrap">{inline(content.configuration)}</Text>
        </div>
      )}

      {/* Environment Variables */}
      {content.environment && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="lock" size="sm" className="mr-2 text-amber-400" />
            Environment Variables
          </Heading>
          <Text variant="secondary" size="sm" className="text-gray-300 font-mono">{inline(content.environment)}</Text>
        </div>
      )}
    </div>
  );
};

// Format Frontend Layer - COMPLETE DATA
const formatFrontendLayer = (content, opts = {}) => {
  const { density = 'comfortable' } = opts;
  const padClass = density === 'compact' ? 'p-3' : 'p-4';
  const gapClass = density === 'compact' ? 'gap-2' : 'gap-3';

  return (
    <div className="space-y-6">
      {/* Framework & Tech Stack */}
      {(content.framework || content.styling) && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="layout" size="sm" className="mr-2 text-cyan-400" />
            Tech Stack
          </Heading>
          <div className="space-y-2">
            {content.framework && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Framework:</span> {inline(content.framework)}</Text>}
            {content.styling && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Styling:</span> {inline(content.styling)}</Text>}
          </div>
        </div>
      )}

      {/* Components - FULL DETAILS */}
      {content.components?.length > 0 && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="dashboard" size="sm" className="mr-2 text-cyan-400" />
            Components ({content.components.length})
          </Heading>
          <div className={`grid ${gapClass}`}>
            {content.components.map((comp, index) => (
              <div key={index} className={`bg-black/20 ${density === 'compact' ? 'p-2' : 'p-3'} rounded-lg border border-white/5`}>
                <Text className="font-medium text-white">{typeof comp === 'string' ? comp : comp.name}</Text>
                {comp.purpose && <Text variant="secondary" size="sm" className="text-gray-400 mt-1">{comp.purpose}</Text>}
                {comp.file && <Text variant="muted" size="xs" className="text-cyan-400/70 font-mono mt-2 break-all">{comp.file}</Text>}
                {comp.props && (
                  <div className="mt-2">
                    <Text variant="muted" size="xs" className="text-gray-500">Props:</Text>
                    <code className="text-xs text-gray-400 bg-black/40 px-2 py-1 rounded block mt-1 font-mono whitespace-pre-wrap break-all">{comp.props}</code>
                  </div>
                )}
                {comp.implementation && (
                  <div className="mt-2">
                    <Text variant="muted" size="xs" className="text-gray-500">Implementation:</Text>
                    <Text size="xs" className="text-gray-400 mt-1">{comp.implementation}</Text>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* State Management */}
      {content.stateManagement && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="refresh" size="sm" className="mr-2 text-cyan-400" />
            State Management
          </Heading>
          <div className="space-y-2">
            {content.stateManagement.pattern && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Pattern:</span> {inline(content.stateManagement.pattern)}</Text>}
            {content.stateManagement.implementation && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Implementation:</span> {inline(content.stateManagement.implementation)}</Text>}
            {content.stateManagement.stateFlow && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">State Flow:</span> {inline(content.stateManagement.stateFlow)}</Text>}
          </div>
        </div>
      )}
    </div>
  );
};

// Format Backend Layer - COMPLETE DATA
const formatBackendLayer = (content, opts = {}) => {
  const { density = 'comfortable' } = opts;
  const padClass = density === 'compact' ? 'p-3' : 'p-4';
  const gapClass = density === 'compact' ? 'gap-2' : 'gap-3';

  return (
    <div className="space-y-6">
      {/* Architecture & Infrastructure */}
      {(content.architecture || content.database || content.authentication) && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="server" size="sm" className="mr-2 text-orange-400" />
            Architecture & Infrastructure
          </Heading>
          <div className="space-y-2">
            {content.architecture && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Architecture:</span> {inline(content.architecture)}</Text>}
            {content.database && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Database:</span> {inline(content.database)}</Text>}
            {content.authentication && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Authentication:</span> {inline(content.authentication)}</Text>}
          </div>
        </div>
      )}

      {/* Endpoints - FULL DETAILS */}
      {content.endpoints?.length > 0 && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="link" size="sm" className="mr-2 text-orange-400" />
            API Endpoints ({content.endpoints.length})
          </Heading>
          <div className={`grid ${gapClass}`}>
            {content.endpoints.map((endpoint, index) => (
              <div key={index} className={`bg-black/20 ${density === 'compact' ? 'p-2' : 'p-3'} rounded-lg border border-white/5`}>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="info" size="xs" className="bg-orange-500/20 text-orange-400 border-orange-500/30">{endpoint.method || 'GET'}</Badge>
                  <Text className="font-mono text-white text-sm">{endpoint.route || endpoint.path}</Text>
                </div>
                {endpoint.purpose && <Text variant="secondary" size="sm" className="text-gray-400">{endpoint.purpose}</Text>}
                {endpoint.file && <Text variant="muted" size="xs" className="text-orange-400/70 font-mono mt-1 break-all">{endpoint.file}</Text>}
                {endpoint.authentication && (
                  <Text size="xs" className="text-gray-500 mt-1"><span className="font-medium">Auth:</span> {endpoint.authentication}</Text>
                )}
                {endpoint.validation && (
                  <Text size="xs" className="text-gray-500 mt-1"><span className="font-medium">Validation:</span> {endpoint.validation}</Text>
                )}
                {endpoint.response && (
                  <Text size="xs" className="text-gray-500 mt-1"><span className="font-medium">Response:</span> {endpoint.response}</Text>
                )}
                {endpoint.middleware?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {endpoint.middleware.map((mw, mwIdx) => (
                      <Badge key={mwIdx} variant="secondary" size="xs" className="bg-white/5 text-gray-400">{mw}</Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Server Actions */}
      {content.serverActions?.length > 0 && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="lightning" size="sm" className="mr-2 text-orange-400" />
            Server Actions ({content.serverActions.length})
          </Heading>
          <div className={`grid ${gapClass}`}>
            {content.serverActions.map((action, index) => (
              <div key={index} className={`bg-black/20 ${density === 'compact' ? 'p-2' : 'p-3'} rounded-lg border border-white/5`}>
                <Text className="font-medium text-white">{typeof action === 'string' ? action : action.name}</Text>
                {action.purpose && <Text variant="secondary" size="sm" className="text-gray-400">{action.purpose}</Text>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Format Deployment Layer - COMPLETE DATA
const formatDeploymentLayer = (content, opts = {}) => {
  const { density = 'comfortable' } = opts;
  const padClass = density === 'compact' ? 'p-3' : 'p-4';

  return (
    <div className="space-y-6">
      {/* Platform & Hosting */}
      {(content.platform || content.hosting || content.database) && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="cloud" size="sm" className="mr-2 text-pink-400" />
            Hosting & Infrastructure
          </Heading>
          <div className="space-y-2">
            {(content.platform || content.hosting) && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Platform:</span> {inline(content.platform || content.hosting)}</Text>}
            {content.database && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Database Hosting:</span> {inline(content.database)}</Text>}
            {content.region && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Region:</span> {inline(content.region)}</Text>}
          </div>
        </div>
      )}

      {/* CI/CD */}
      {(content.cicd || content.pipeline) && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="refresh" size="sm" className="mr-2 text-pink-400" />
            CI/CD Pipeline
          </Heading>
          <Text variant="secondary" className="text-gray-300">{inline(content.cicd || content.pipeline)}</Text>
        </div>
      )}

      {/* Monitoring */}
      {content.monitoring && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="chart" size="sm" className="mr-2 text-pink-400" />
            Monitoring & Analytics
          </Heading>
          <Text variant="secondary" className="text-gray-300">{inline(content.monitoring)}</Text>
        </div>
      )}

      {/* Environment */}
      {content.environment && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="lock" size="sm" className="mr-2 text-pink-400" />
            Environment Management
          </Heading>
          <Text variant="secondary" className="text-gray-300">{inline(content.environment)}</Text>
        </div>
      )}

      {/* Infrastructure */}
      {content.infrastructure && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="server" size="sm" className="mr-2 text-pink-400" />
            Infrastructure
          </Heading>
          <Text variant="secondary" className="text-gray-300">{inline(content.infrastructure)}</Text>
        </div>
      )}

      {/* Scaling */}
      {content.scaling && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="arrowUp" size="sm" className="mr-2 text-pink-400" />
            Scaling Strategy
          </Heading>
          <Text variant="secondary" className="text-gray-300">{inline(content.scaling)}</Text>
        </div>
      )}
    </div>
  );
};

// Format Testing Strategy layer - COMPLETE DATA with nested structure
const formatTestingStrategy = (content, opts = {}) => {
  const { density = 'comfortable' } = opts;
  const padClass = density === 'compact' ? 'p-3' : 'p-4';

  // Handle nested structure: unitTesting.framework, unitTesting.coverage, etc.
  const unitTesting = content.unitTesting || content.unitTests || content.unit;
  const integrationTesting = content.integrationTesting || content.integrationTests || content.integration;
  const e2eTesting = content.e2eTesting || content.e2eTests || content.e2e;

  return (
    <div className="space-y-6">
      {/* Unit Tests */}
      {unitTesting && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="check" size="sm" className="mr-2 text-emerald-400" />
            Unit Testing
          </Heading>
          {typeof unitTesting === 'object' ? (
            <div className="space-y-2">
              {unitTesting.framework && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Framework:</span> {inline(unitTesting.framework)}</Text>}
              {unitTesting.coverage && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Coverage:</span> {inline(unitTesting.coverage)}</Text>}
              {unitTesting.approach && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Approach:</span> {inline(unitTesting.approach)}</Text>}
            </div>
          ) : (
            <Text variant="secondary" className="text-gray-300">{inline(unitTesting)}</Text>
          )}
        </div>
      )}

      {/* Integration Tests */}
      {integrationTesting && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="link" size="sm" className="mr-2 text-emerald-400" />
            Integration Testing
          </Heading>
          {typeof integrationTesting === 'object' ? (
            <div className="space-y-2">
              {integrationTesting.framework && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Framework:</span> {inline(integrationTesting.framework)}</Text>}
              {integrationTesting.scope && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Scope:</span> {inline(integrationTesting.scope)}</Text>}
              {integrationTesting.approach && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Approach:</span> {inline(integrationTesting.approach)}</Text>}
            </div>
          ) : (
            <Text variant="secondary" className="text-gray-300">{inline(integrationTesting)}</Text>
          )}
        </div>
      )}

      {/* E2E Tests */}
      {e2eTesting && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="cursor" size="sm" className="mr-2 text-emerald-400" />
            End-to-End Testing
          </Heading>
          {typeof e2eTesting === 'object' ? (
            <div className="space-y-2">
              {e2eTesting.framework && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Framework:</span> {inline(e2eTesting.framework)}</Text>}
              {e2eTesting.approach && <Text size="sm" className="text-gray-300"><span className="font-bold text-gray-200">Approach:</span> {inline(e2eTesting.approach)}</Text>}
              {e2eTesting.scenarios && (
                <div className="mt-2">
                  <Text variant="muted" size="xs" className="text-gray-500 mb-1">Test Scenarios:</Text>
                  <div className="grid gap-1">
                    {Array.isArray(e2eTesting.scenarios)
                      ? e2eTesting.scenarios.map((scenario, idx) => (
                        <Text key={idx} size="xs" className="text-gray-400">• {scenario}</Text>
                      ))
                      : <Text size="xs" className="text-gray-400">{e2eTesting.scenarios}</Text>
                    }
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Text variant="secondary" className="text-gray-300">{inline(e2eTesting)}</Text>
          )}
        </div>
      )}

      {/* Legacy fields for backward compatibility */}
      {content.framework && !unitTesting?.framework && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="settings" size="sm" className="mr-2 text-emerald-400" />
            Test Framework
          </Heading>
          <Text variant="secondary" className="text-gray-300">{inline(content.framework)}</Text>
        </div>
      )}

      {content.coverage && !unitTesting?.coverage && (
        <div className={`glass-panel rounded-xl ${padClass}`}>
          <Heading level={4} className="font-semibold mb-3 flex items-center text-white">
            <Icon name="chart" size="sm" className="mr-2 text-emerald-400" />
            Coverage Goals
          </Heading>
          <Text variant="secondary" className="text-gray-300">{inline(content.coverage)}</Text>
        </div>
      )}
    </div>
  );
};


// LayerComponent - Reusable component for displaying each layer
const LayerComponent = ({
  title,
  description,
  content,
  isExpanded,
  onToggle,
  onCopy,
  copyStatus,
  layerKey
}) => {

  // Check if content is truly empty (null, undefined, or empty object)
  const hasContent = content && (
    typeof content === 'string' ||
    (typeof content === 'object' && Object.keys(content).length > 0)
  );

  if (!hasContent) {
    return (
      <div className="glass-panel p-4 sm:p-6 rounded-2xl">
        <div className="flex items-center justify-between">
          <div>
            <Heading level={3} className="text-base sm:text-lg text-white">{title}</Heading>
            <Text variant="secondary" size="sm" className="text-gray-400">{description}</Text>
          </div>
          <Text variant="muted" size="sm" className="text-gray-500">No data available</Text>
        </div>
      </div>
    );
  }

  // Format content for professional display
  const formatLayerContent = (content, layerKey) => {
    if (!content) return null;

    switch (layerKey) {
      case 'presentationLayer':
        return formatPresentationLayer(content);
      case 'businessLayer':
        return formatBusinessLayer(content);
      case 'persistenceLayer':
        return formatPersistenceLayer(content);
      case 'databaseLayer':
        return formatDatabaseLayer(content);
      case 'securityLayer':
        return formatSecurityLayer(content);
      default:
        return <pre className="whitespace-pre-wrap text-sm text-gray-300 font-mono overflow-x-auto bg-black/40 p-4 rounded-lg border border-white/5">{JSON.stringify(content, null, 2)}</pre>;
    }
  };

  const contentText = typeof content === 'object' ? JSON.stringify(content, null, 2) : content;
  const formattedContent = formatLayerContent(content, layerKey);

  return (
    <div className="glass-panel overflow-hidden rounded-2xl">
      <div
        className="p-4 sm:p-6 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <Heading level={3} className="text-base sm:text-lg flex items-center space-x-2 text-white">
              <span>{title}</span>
            </Heading>
            <Text variant="secondary" size="sm" className="mt-1 text-gray-400">{description}</Text>

            {/* Show data summary */}
            {content && (
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {content.detailedComponents?.count > 0 && (
                  <Badge variant="info" size="xs" className="bg-[#7B61FF]/10 text-[#7B61FF] border-[#7B61FF]/20">
                    {content.detailedComponents.count} Components
                  </Badge>
                )}
                {content.detailedEndpoints?.count > 0 && (
                  <Badge variant="success" size="xs" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                    {content.detailedEndpoints.count} Endpoints
                  </Badge>
                )}
                {content.schema?.tables?.length > 0 && (
                  <Badge variant="info" size="xs" className="bg-[#7B61FF]/10 text-[#7B61FF] border-[#7B61FF]/20">
                    {content.schema.tables.length} Tables
                  </Badge>
                )}
                {content.projectStructure?.folders?.length > 0 && (
                  <Badge variant="warning" size="xs" className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                    {content.projectStructure.folders.length} Folders
                  </Badge>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onCopy(contentText);
              }}
              variant="ghost"
              size="sm"
              className={`p-2 ${copyStatus === 'success'
                ? 'text-green-400'
                : copyStatus === 'error'
                  ? 'text-red-400'
                  : 'text-gray-400 hover:text-[#7B61FF] hover:bg-white/10'
                }`}
            >
              <Icon
                name={copyStatus === 'success' ? 'check' : copyStatus === 'error' ? 'x' : 'clipboard'}
                size="xs"
              />
            </Button>
            <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
              <Icon name="arrowUp" size="xs" className="text-gray-500 rotate-180" />
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-white/5 p-4 sm:p-6 bg-black/20">
          {formattedContent || (
            <pre className="whitespace-pre-wrap text-sm text-gray-300 font-mono overflow-x-auto bg-black/40 p-4 rounded-lg border border-white/5">
              {contentText}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default TechnicalArchitecture;